/**
 * Hook personalizado para gestionar lógica de compras
 * Maneja: crear, actualizar, obtener, agregar/eliminar retenciones
 */

import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3002') + '/api';

const getAuthHeaders = (empresaId) => {
  const token = localStorage.getItem('token');
  return {
    'empresa-id': empresaId,
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const useCompras = (empresaId) => {
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Crear compra completa
  const crearCompra = useCallback(
    async (compraData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/compras-dinamico/crear-completa`,
          compraData,
          { headers: getAuthHeaders(empresaId) }
        );

        const nuevaCompra = response.data.data;
        setCompras((prev) => [nuevaCompra, ...prev]);
        return nuevaCompra;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Obtener compras
  const cargarCompras = useCallback(
    async (filtros = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
        if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
        if (filtros.estado) params.append('estado', filtros.estado);
        if (filtros.tipo_compra) params.append('tipo_compra', filtros.tipo_compra);

        const response = await axios.get(
          `${API_BASE_URL}/compras-dinamico?${params.toString()}`,
          { headers: getAuthHeaders(empresaId) }
        );

        setCompras(response.data.data || []);
        return response.data.data;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Obtener compra por ID
  const obtenerCompra = useCallback(
    async (compraId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/compras-dinamico/${compraId}`,
          { headers: getAuthHeaders(empresaId) }
        );

        return response.data.data;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Agregar retención
  const agregarRetencion = useCallback(
    async (compraId, retencionData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/compras-dinamico/${compraId}/retenciones`,
          retencionData,
          {
            headers: {
              'empresa-id': empresaId,
              'Content-Type': 'application/json',
            },
          }
        );

        const compraActualizada = response.data.data;
        setCompras((prev) =>
          prev.map((c) => (c.id === compraId ? compraActualizada : c))
        );
        return compraActualizada;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Eliminar retención
  const eliminarRetencion = useCallback(
    async (compraId, retencionId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/compras-dinamico/${compraId}/retenciones/${retencionId}`,
          {
            headers: {
              'empresa-id': empresaId,
            },
          }
        );

        const compraActualizada = response.data.data;
        setCompras((prev) =>
          prev.map((c) => (c.id === compraId ? compraActualizada : c))
        );
        return compraActualizada;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Limpiar error
  const limpiarError = useCallback(() => {
    setError(null);
  }, []);

  return {
    compras,
    loading,
    error,
    crearCompra,
    cargarCompras,
    obtenerCompra,
    agregarRetencion,
    eliminarRetencion,
    limpiarError,
  };
};
